import { Metadata, type CountryCode } from 'libphonenumber-js';

/**
 * Uses E.164 format as the standard: +[country code][number]
 * 
 * 1. Always store in E.164 format in database (+14043072351)
 */

interface PhoneValidation {
  isValid: boolean;
  e164: string | null;
  countryCode: string | null;
  nationalNumber: string | null;
  error?: string;
}

const COUNTRY_DATA: Record<string, { name: string; lengths: number[]; format?: string }> = {
  '1': { 
    name: 'US/CA', 
    lengths: [10], // US/Canada always 10 digits after country code
    format: '+1 XXX-XXX-XXXX' 
  },
  '44': { 
    name: 'UK', 
    lengths: [10, 11], // UK can be 10 or 11 digits
    format: '+44 XXXX XXXXXX' 
  },
  '33': { 
    name: 'France', 
    lengths: [9], 
    format: '+33 X XX XX XX XX' 
  },
  '49': { 
    name: 'Germany', 
    lengths: [10, 11], 
    format: '+49 XXX XXXXXXXX' 
  },
  '81': { 
    name: 'Japan', 
    lengths: [9, 10], 
    format: '+81 XX-XXXX-XXXX' 
  },
  '86': { 
    name: 'China', 
    lengths: [11], 
    format: '+86 XXX XXXX XXXX' 
  },
  '91': { 
    name: 'India', 
    lengths: [10], 
    format: '+91 XXXXX XXXXX' 
  },
  '234': { 
    name: 'Nigeria', 
    lengths: [10], 
    format: '+234 XXX XXX XXXX' 
  },
  '971': { 
    name: 'UAE', 
    lengths: [8, 9], 
    format: '+971 XX XXX XXXX' 
  },
  '65': { 
    name: 'Singapore', 
    lengths: [8], 
    format: '+65 XXXX XXXX' 
  },
  '61': { 
    name: 'Australia', 
    lengths: [9], 
    format: '+61 X XXXX XXXX' 
  },
  '55': { 
    name: 'Brazil', 
    lengths: [10, 11], 
    format: '+55 XX XXXXX-XXXX' 
  }
};

export function normalizeToE164(
  phone: string, 
  defaultCountryCode: string = '1'
): PhoneValidation {
  let cleaned = phone.replace(/[^\d+]/g, '');
  
  if (!cleaned.startsWith('+')) {
    if (cleaned.startsWith(defaultCountryCode)) {
      cleaned = '+' + cleaned;
    } else if (cleaned.length === 10 && defaultCountryCode === '1') {
      cleaned = '+1' + cleaned;
    } else if (cleaned.length < 10) {
      // Too short, likely missing country code
      cleaned = '+' + defaultCountryCode + cleaned;
    } else {
      // Assume it's a full number missing just the +
      cleaned = '+' + cleaned;
    }
  }
  
  const countryCode = extractCountryCode(cleaned);
  if (!countryCode) {
    return {
      isValid: false,
      e164: null,
      countryCode: null,
      nationalNumber: null,
      error: 'Invalid country code'
    };
  }
  
  const nationalNumber = cleaned.substring(countryCode.length + 1); // +[code][number]
  const countryInfo = COUNTRY_DATA[countryCode];
  
  if (!countryInfo) {
    if (nationalNumber.length < 4 || nationalNumber.length > 15) {
      return {
        isValid: false,
        e164: null,
        countryCode,
        nationalNumber,
        error: 'Invalid phone number length'
      };
    }
  } else {
    if (!countryInfo.lengths.includes(nationalNumber.length)) {
      return {
        isValid: false,
        e164: null,
        countryCode,
        nationalNumber,
        error: `Invalid ${countryInfo.name} phone number (expected ${countryInfo.lengths.join(' or ')} digits)`
      };
    }
  }
  
  return {
    isValid: true,
    e164: cleaned,
    countryCode,
    nationalNumber
  };
}

function extractCountryCode(e164: string): string | null {
  if (!e164.startsWith('+')) return null;
  
  for (let len = 3; len >= 1; len--) {
    const code = e164.substring(1, len + 1);
    if (COUNTRY_DATA[code]) {
      return code;
    }
  }
  
  // If not found in our data, try to guess based on common patterns
  // This is a fallback for countries we haven't explicitly added
  const withoutPlus = e164.substring(1);
  if (withoutPlus.length >= 10 && withoutPlus.length <= 15) {
    // Assume 1-digit code if total length is 11
    if (withoutPlus.length === 11) return withoutPlus.substring(0, 1);
    // Assume 2-digit code if total length is 12
    if (withoutPlus.length === 12) return withoutPlus.substring(0, 2);
    return withoutPlus.substring(0, 3);
  }
  
  return null;
}

export function formatForDisplay(e164: string): string {
  const validation = normalizeToE164(e164);
  if (!validation.isValid || !validation.e164) return e164;
  
  const countryInfo = COUNTRY_DATA[validation.countryCode!];
  if (!countryInfo || !countryInfo.format) {
    const cc = validation.countryCode!;
    const num = validation.nationalNumber!;
    if (num.length <= 7) {
      return `+${cc} ${num}`;
    } else if (num.length <= 10) {
      return `+${cc} ${num.slice(0, 3)} ${num.slice(3, 6)} ${num.slice(6)}`;
    } else {
      return `+${cc} ${num.slice(0, 3)} ${num.slice(3, 7)} ${num.slice(7)}`;
    }
  }
  
  let formatted = countryInfo.format;
  let numIndex = 0;
  
  formatted = formatted.replace(/X/g, () => {
    if (numIndex < validation.nationalNumber!.length) {
      return validation.nationalNumber![numIndex++];
    }
    return '';
  });
  
  return formatted.replace(/[-\s]+$/, '');
}

/**
 * Always returns E.164 format or null
 */
export function prepareForStorage(phone: string, defaultCountryCode: string = '1'): string | null {
  const validation = normalizeToE164(phone, defaultCountryCode);
  return validation.isValid ? validation.e164 : null;
}

function trunkPrefixFor(countryCode: string): string | null {
  try {
    const metadata = new Metadata();
    metadata.selectNumberingPlan(countryCode as CountryCode);
    const numberingPlan = metadata.numberingPlan as
      | (NonNullable<typeof metadata.numberingPlan> & { nationalPrefix: () => unknown })
      | undefined;
    const prefix = numberingPlan?.nationalPrefix();
    return typeof prefix === 'string' && prefix ? prefix : null;
  } catch {
    return null;
  }
}

/** Country picker + typed local number → E.164, dropping a national trunk prefix or repeated country code. */
export function composeE164FromLocal(countryCode: string, localInput: string): string | null {
  const digits = (localInput || '').replace(/\D+/g, '');
  if (!digits) return null;
  const trunk = trunkPrefixFor(countryCode);
  if (trunk && digits.startsWith(trunk)) {
    const stripped = prepareForStorage(`+${countryCode}${digits.slice(trunk.length)}`);
    if (stripped) return stripped;
  }
  const direct = prepareForStorage(`+${countryCode}${digits}`);
  if (direct) return direct;
  if (digits.startsWith(countryCode)) return prepareForStorage(`+${digits}`);
  return null;
}

export function isSameNumber(phone1: string, phone2: string): boolean {
  const norm1 = normalizeToE164(phone1);
  const norm2 = normalizeToE164(phone2);
  
  if (!norm1.isValid || !norm2.isValid) return false;
  return norm1.e164 === norm2.e164;
}

export function getCountryCode(phone: string): string | null {
  const validation = normalizeToE164(phone);
  return validation.countryCode;
}

export function validatePhone(phone: string, defaultCountryCode: string = '1'): {
  valid: boolean;
  formatted: string | null;
  error?: string;
} {
  const validation = normalizeToE164(phone, defaultCountryCode);
  
  if (!validation.isValid) {
    return {
      valid: false,
      formatted: null,
      error: validation.error || 'Invalid phone number'
    };
  }
  
  return {
    valid: true,
    formatted: validation.e164,
    error: undefined
  };
}

export function getSupportedCountries(): Array<{
  code: string;
  name: string;
  display: string;
}> {
  return Object.entries(COUNTRY_DATA)
    .map(([code, info]) => ({
      code,
      name: info.name,
      display: `+${code} ${info.name}`
    }))
    .sort((a, b) => {
      if (a.code === '1') return -1;
      if (b.code === '1') return 1;
      return parseInt(a.code) - parseInt(b.code);
    });
}